import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  CheckCircle,
  Circle,
  CalendarIcon,
  ListTodo
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths } from 'date-fns';
import { pt } from 'date-fns/locale';

const TASK_TYPES = [
  { id: 'tarefa', label: 'Tarefa', color: 'bg-blue-500' },
  { id: 'reuniao', label: 'Reunião', color: 'bg-purple-500' },
  { id: 'chamada', label: 'Chamada', color: 'bg-green-500' },
  { id: 'deadline', label: 'Deadline', color: 'bg-red-500' },
  { id: 'lembrete', label: 'Lembrete', color: 'bg-amber-500' }
];

const PRIORITIES = [
  { id: 'baixa', label: 'Baixa', color: 'text-slate-500' },
  { id: 'media', label: 'Média', color: 'text-blue-500' },
  { id: 'alta', label: 'Alta', color: 'text-amber-500' },
  { id: 'urgente', label: 'Urgente', color: 'text-red-500' }
];

export default function CalendarPage() {
  const { api } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    task_type: 'tarefa',
    due_date: format(new Date(), 'yyyy-MM-dd'),
    due_time: '',
    priority: 'media',
    status: 'pendente'
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await api().get('/tasks');
      setTasks(response.data);
    } catch (error) {
      toast.error('Erro ao carregar tarefas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTask) {
        await api().put(`/tasks/${editingTask.id}`, form);
        toast.success('Tarefa atualizada!');
      } else {
        await api().post('/tasks', form);
        toast.success('Tarefa criada!');
      }
      
      setShowModal(false);
      resetForm();
      loadTasks();
    } catch (error) {
      toast.error('Erro ao guardar tarefa');
    }
  };

  const handleStatusToggle = async (task) => {
    const newStatus = task.status === 'concluida' ? 'pendente' : 'concluida';
    try {
      await api().patch(`/tasks/${task.id}/status`, { status: newStatus });
      loadTasks();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm(`Eliminar "${task.title}"?`)) return;
    
    try {
      await api().delete(`/tasks/${task.id}`);
      toast.success('Tarefa eliminada!');
      loadTasks();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setForm({
      title: task.title || '',
      description: task.description || '',
      task_type: task.task_type || 'tarefa',
      due_date: task.due_date || format(new Date(), 'yyyy-MM-dd'),
      due_time: task.due_time || '',
      priority: task.priority || 'media',
      status: task.status || 'pendente'
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      task_type: 'tarefa',
      due_date: format(selectedDate, 'yyyy-MM-dd'),
      due_time: '',
      priority: 'media',
      status: 'pendente'
    });
    setEditingTask(null);
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => 
      task.due_date && isSameDay(new Date(task.due_date), date)
    );
  };

  const selectedDateTasks = getTasksForDate(selectedDate);

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const openNewTaskModal = () => {
    resetForm();
    setForm(prev => ({ ...prev, due_date: format(selectedDate, 'yyyy-MM-dd') }));
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold font-[Manrope] capitalize min-w-[180px] text-center">
            {format(currentMonth, 'MMMM yyyy', { locale: pt })}
          </h2>
          <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button 
          className="bg-indigo-800 hover:bg-indigo-900"
          onClick={openNewTaskModal}
          data-testid="new-task-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Tarefa
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardContent className="p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              locale={pt}
              className="w-full"
              classNames={{
                months: "w-full",
                month: "w-full",
                table: "w-full border-collapse",
                head_row: "",
                head_cell: "text-muted-foreground font-medium text-sm w-[14.28%] py-2",
                row: "",
                cell: "relative p-0 text-center w-[14.28%] h-24 border border-slate-100",
                day: "h-full w-full p-1 font-normal hover:bg-slate-50 flex flex-col items-start",
                day_selected: "bg-indigo-50 text-indigo-900",
                day_today: "bg-amber-50",
                day_outside: "text-muted-foreground opacity-50",
              }}
              components={{
                Day: ({ date, ...props }) => {
                  const dayTasks = getTasksForDate(date);
                  const isSelected = isSameDay(date, selectedDate);
                  const isToday = isSameDay(date, new Date());
                  
                  return (
                    <button
                      {...props}
                      onClick={() => handleDateSelect(date)}
                      className={`
                        h-full w-full p-1 flex flex-col items-start
                        hover:bg-slate-50 transition-colors
                        ${isSelected ? 'bg-indigo-50' : ''}
                        ${isToday ? 'bg-amber-50' : ''}
                      `}
                    >
                      <span className={`
                        text-sm font-medium mb-1
                        ${isToday ? 'text-amber-600' : ''}
                        ${isSelected ? 'text-indigo-800' : ''}
                      `}>
                        {format(date, 'd')}
                      </span>
                      <div className="space-y-0.5 w-full">
                        {dayTasks.slice(0, 3).map((task) => {
                          const taskType = TASK_TYPES.find(t => t.id === task.task_type);
                          return (
                            <div
                              key={task.id}
                              className={`text-xs px-1 py-0.5 rounded truncate text-white ${taskType?.color || 'bg-slate-500'}`}
                            >
                              {task.title}
                            </div>
                          );
                        })}
                        {dayTasks.length > 3 && (
                          <div className="text-xs text-muted-foreground">
                            +{dayTasks.length - 3} mais
                          </div>
                        )}
                      </div>
                    </button>
                  );
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Selected Day Tasks */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold font-[Manrope]">
                {format(selectedDate, "d 'de' MMMM", { locale: pt })}
              </h3>
              <Badge variant="secondary">{selectedDateTasks.length} tarefas</Badge>
            </div>
            
            <div className="space-y-3 max-h-[calc(100vh-400px)] overflow-y-auto">
              {selectedDateTasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ListTodo className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Nenhuma tarefa para este dia</p>
                  <Button variant="link" className="mt-2" onClick={openNewTaskModal}>
                    Adicionar tarefa
                  </Button>
                </div>
              ) : (
                selectedDateTasks.map((task) => {
                  const taskType = TASK_TYPES.find(t => t.id === task.task_type);
                  const priority = PRIORITIES.find(p => p.id === task.priority);
                  
                  return (
                    <div
                      key={task.id}
                      className={`
                        p-3 border rounded-lg cursor-pointer hover:shadow-sm transition-all
                        ${task.status === 'concluida' ? 'bg-slate-50 opacity-60' : ''}
                      `}
                      onClick={() => handleEdit(task)}
                      data-testid={`task-item-${task.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleStatusToggle(task); }}
                          className="mt-0.5"
                        >
                          {task.status === 'concluida' ? (
                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                          ) : (
                            <Circle className="h-5 w-5 text-slate-300 hover:text-indigo-500" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <div className={`w-2 h-2 rounded-full ${taskType?.color || 'bg-slate-500'}`} />
                            <span className="text-xs text-muted-foreground">{taskType?.label}</span>
                          </div>
                          <p className={`font-medium text-sm ${task.status === 'concluida' ? 'line-through' : ''}`}>
                            {task.title}
                          </p>
                          {task.due_time && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3" />
                              {task.due_time}
                            </p>
                          )}
                          <Badge variant="outline" className={`text-xs mt-2 ${priority?.color}`}>
                            {priority?.label}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              {editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  data-testid="task-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="task_type">Tipo</Label>
                  <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                    <SelectTrigger data-testid="task-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${type.color}`} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Prioridade</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((priority) => (
                        <SelectItem key={priority.id} value={priority.id}>
                          {priority.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="due_date">Data</Label>
                  <Input
                    id="due_date"
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                    data-testid="task-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_time">Hora</Label>
                  <Input
                    id="due_time"
                    type="time"
                    value={form.due_time}
                    onChange={(e) => setForm({ ...form, due_time: e.target.value })}
                    data-testid="task-time-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="task-description-input"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              {editingTask && (
                <Button 
                  type="button" 
                  variant="destructive"
                  onClick={() => { handleDelete(editingTask); setShowModal(false); }}
                >
                  Eliminar
                </Button>
              )}
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-indigo-800 hover:bg-indigo-900" data-testid="save-task-btn">
                {editingTask ? 'Guardar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
